import { useEffect, useState } from 'react';
import { TimePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import { FILTER_FIELD_TEXTS } from '../../../../components/shared/filterFieldTexts';
import filterDefaults from '../../../../components/shared/filterPanelDefaults.module.css';
import {
  AttemptTimeRangeDraft,
  getAttemptTimeDisabledOptions,
  normalizeAttemptTimeRangeDraft,
} from './attemptTimeRange';

type ActiveRange = 'start' | 'end' | null;

interface Props {
  value: [Dayjs, Dayjs] | null;
  onChange: (value: [Dayjs, Dayjs] | null) => void;
  disabled: boolean;
}

export const AttemptTimeRangeFilter = ({
  value,
  onChange,
  disabled,
}: Props) => {
  const [draft, setDraft] = useState<AttemptTimeRangeDraft>(value);
  const [activeRange, setActiveRange] = useState<ActiveRange>(null);
  const [attemptFromConfirmed, setAttemptFromConfirmed] = useState(
    Boolean(value?.[0]),
  );

  useEffect(() => {
    setDraft(value);
    setAttemptFromConfirmed(Boolean(value?.[0]));
  }, [value]);

  let disabledFields: [boolean, boolean];

  if (disabled) {
    disabledFields = [true, true];
  } else if (activeRange === 'start') {
    disabledFields = [false, true];
  } else if (activeRange === 'end') {
    disabledFields = [true, false];
  } else {
    disabledFields = [false, !attemptFromConfirmed];
  }

  return (
    <div className={filterDefaults.filterInputsContainer}>
      <label className={filterDefaults.filterLabel}>
        {FILTER_FIELD_TEXTS.callPeriod.label}
      </label>
      <TimePicker.RangePicker
        allowClear
        allowEmpty={[true, true]}
        className={filterDefaults.filterInput}
        value={draft}
        format="HH:mm"
        needConfirm={true}
        order={false}
        placeholder={['С', 'По']}
        disabled={disabledFields}
        disabledTime={(date, range, info) =>
          getAttemptTimeDisabledOptions(date, range, {
            from: info.from ?? draft?.[0] ?? undefined,
          })
        }
        onFocus={(_event, info) => {
          setActiveRange(info.range ?? null);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setActiveRange(null);
          }
        }}
        onCalendarChange={(nextValue, _dateStrings, info) => {
          const nextDraft = normalizeAttemptTimeRangeDraft(nextValue);

          setDraft(nextDraft);

          if (info.range === 'start') {
            setAttemptFromConfirmed(false);
          }
        }}
        onOk={(nextValue) => {
          const nextDraft = normalizeAttemptTimeRangeDraft(nextValue);
          const attemptFrom = nextDraft?.[0];
          const attemptTo = nextDraft?.[1];

          setDraft(nextDraft);

          if (activeRange === 'start') {
            setAttemptFromConfirmed(Boolean(attemptFrom));
          }

          if (
            attemptFrom &&
            attemptTo &&
            (attemptFrom.format('HH:mm') !== value?.[0].format('HH:mm') ||
              attemptTo.format('HH:mm') !== value?.[1].format('HH:mm'))
          ) {
            onChange([attemptFrom, attemptTo]);
          }
        }}
        onChange={(nextValue) => {
          const nextDraft = normalizeAttemptTimeRangeDraft(nextValue);
          const attemptFrom = nextDraft?.[0];

          setDraft(nextDraft);

          if (!nextValue) {
            setAttemptFromConfirmed(false);
            onChange(null);
            return;
          }

          if (!attemptFrom) {
            setAttemptFromConfirmed(false);
            return;
          }

          setAttemptFromConfirmed(true);
        }}
      />
    </div>
  );
};
